import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Form, Input, message, Tabs, Upload } from 'antd';
import Dragger from 'antd/lib/upload/Dragger';

import { InboxOutlined, LoadingOutlined } from '@ant-design/icons';

import UploadsPreview from '../../UploadsPreview';
import { isValidUrl, uploadImageToS3 } from '../../../utils/helpers';
import { hasFeature } from '../../../utils/restrictions';
import { showUpselling } from '../../../store/actions/paymentActions';
import SettingsFooter from '../SettingsFooter';
import './style.less';

const SeoSettings = (props) => {
  const dispatch = useDispatch();

  const {
    reset,
    control,
    handleSubmit,
    formState: { dirtyFields, isDirty },
    setValue,
  } = useForm({
    defaultValues: {
      meta: (props?.plan !== 'free' && props?.form?.meta) || {
        title: '',
        desc: '',
        image: '',
        imageType: '',
      },
    },
    mode: 'onChange',
  });
  const [seoImage, setSeoImage] = useState(props?.form?.meta.image);
  const [seoImageType, setSeoImageType] = useState(
    props?.plan !== 'free' ? props?.form?.meta.imageType : 'image/png',
  );

  const [uploading, setUploading] = useState(false);

  const { t } = useTranslation();

  props.dirtyFieldsRef.current = dirtyFields;

  useEffect(() => {
    reset({
      meta: (props?.plan !== 'free' && props?.form?.meta) || {
        title: 'Whatsform',
        desc: "Create a free form using WhatsForm & Get data directly from the customer's WhatsApp number",
        image: 'https://whatsform.com/assets/images/meta.png',
        imageType: 'image/png',
      },
    });
    if (props?.form?.meta && props?.plan !== 'free') {
      setSeoImage(props?.form?.meta.image);
      setSeoImageType(props?.form?.meta.imageType);
    } else {
      setSeoImage('https://whatsform.com/assets/images/meta.png');
      setSeoImageType('image/png');
    }
  }, [props.form, props.plan, reset]);

  function handleSave(data) {
    props.handleDispatch(data);
  }

  function handleError(error) {
    console.log(error);
    message.error(t('formSettings.updateFail'));
  }

  const beforeUploadHandler = (file) => {
    if (!hasFeature(props.plan, 'customSeo')) {
      return dispatch(showUpselling('meta'));
    } else if (!['image/png', 'image/jpg', 'image/jpeg', 'image/svg+xml'].includes(file.type)) {
      message.error(`${file.type} is not supported`);
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const mediaChangeHandler = (url, type) => {
    if (!hasFeature(props.plan, 'customSeo')) return dispatch(showUpselling('meta'));
    if (!url) {
      setSeoImage('');
      setSeoImageType('');
      return setValue('meta', { image: '' }, { shouldDirty: true });
    }
    if (isValidUrl(url)) {
      setSeoImage(url);
      setSeoImageType(type);
      return setValue('meta', { image: url, imageType: type }, { shouldDirty: true });
    }
  };

  const uploadMetaImage = async (options) => {
    const fileName = `meta/${props?.form.id}.${options.file.name
      .replace(/^.*\./, '')
      .toLowerCase()}`;
    const uploadResponse = await uploadImageToS3(fileName, setUploading, options);
    const { type, url } = uploadResponse;
    setSeoImage(url);
    setSeoImageType(type);
    setValue('meta', { image: url, imageType: type }, { shouldDirty: true });
  };

  return (
    <div className="form-settings-screen form-seo-settings">
      <Form
        className="form-settings-inputs"
        colon={false}
        labelCol={{
          span: 8,
        }}
        labelAlign="left"
      >
        <Controller
          render={({ field }) => (
            <Form.Item label={t('formSettings.seoTitle')}>
              <Input
                {...field}
                onChange={
                  !hasFeature(props.plan, 'customSeo')
                    ? () => {
                        return dispatch(showUpselling('meta'));
                      }
                    : field.onChange
                }
              />
            </Form.Item>
          )}
          control={control}
          name="meta.title"
        />
        <Controller
          render={({ field }) => (
            <Form.Item label={t('formSettings.seoDesc')}>
              <Input
                {...field}
                onChange={
                  !hasFeature(props.plan, 'customSeo')
                    ? () => {
                        return dispatch(showUpselling('meta'));
                      }
                    : field.onChange
                }
              />
            </Form.Item>
          )}
          control={control}
          name="meta.desc"
        />
        {/* <Controller
          render={({ field }) => (
            <Form.Item label={t('formSettings.seoImage')}>
              <Input
                {...field}
                onChange={
                  !hasFeature(props.plan, 'customSeo')
                    ? () => {
                        return dispatch(showUpselling('meta'));
                      }
                    : field.onChange
                }
              />
            </Form.Item>
          )}
          control={control}
          name="meta.image"
        /> */}

        <Form.Item label={t('formSettings.seoImage')}>
          <Tabs
            defaultActiveKey={seoImageType === 'url' ? 'link' : 'upload'}
            style={{ marginTop: -8 }}
          >
            <Tabs.TabPane tab={t('formSettings.seoUpload')} key="upload">
              <div>
                {/* hide if image was added using upload and not url */}
                {(!!seoImageType && seoImageType === 'url') || !seoImage ? (
                  <Dragger
                    multiple={false}
                    disabled={uploading}
                    maxCount={1}
                    showUploadList={false}
                    beforeUpload={(file) => beforeUploadHandler(file)}
                    customRequest={(options) => uploadMetaImage(options)}
                  >
                    <p className="ant-upload-drag-icon">
                      {uploading ? <LoadingOutlined /> : <InboxOutlined />}
                    </p>
                    <p className="ant-upload-text">{t('formSettings.seoUploadText')}</p>
                  </Dragger>
                ) : (
                  <>
                    <UploadsPreview
                      style={{ height: '150px' }}
                      url={seoImage}
                      remove={() => mediaChangeHandler()}
                    />
                  </>
                )}
              </div>
            </Tabs.TabPane>
            <Tabs.TabPane tab={t('formSettings.seoLink')} key="link">
              <Controller
                render={({ field }) => (
                  <Input
                    size="large"
                    {...field}
                    placeholder={t('formSettings.seoLinkPlaceholder')}
                    onChange={(e) => {
                      if (!hasFeature(props.plan, 'customSeo'))
                        return dispatch(showUpselling('meta'));
                      else {
                        field.onChange(e);
                        mediaChangeHandler(e.target.value, 'url');
                      }
                    }}
                  />
                )}
                name="meta.image"
                control={control}
              />
            </Tabs.TabPane>
          </Tabs>
        </Form.Item>
        {/*  */}
      </Form>
      <SettingsFooter
        isDirty={isDirty}
        handleSubmit={handleSubmit(handleSave, handleError)}
        loading={props.isLoading}
      />
    </div>
  );
};

export default SeoSettings;
